import React from 'react';
import { Search, X, Download, Plus, Store } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useGetApiWorkspacesWorkspaceIdStores } from '@/api/generated/stores/stores';
import { extractStoresArray } from '@/lib/utils';
import { toast } from 'sonner';
import type { StoreItem, CustomerInfo } from '@/types';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

interface CustomerFiltersProps {
  search: string;
  onSearchChange: (val: string) => void;
  selectedStoreId: string;
  onStoreChange: (val: string) => void;
  customers: CustomerInfo[];
  onAddCustomerClick: () => void;
}

export const CustomerFilters: React.FC<CustomerFiltersProps> = ({
  search,
  onSearchChange,
  selectedStoreId,
  onStoreChange,
  customers,
  onAddCustomerClick,
}) => {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { data: stores } = useGetApiWorkspacesWorkspaceIdStores(activeWorkspaceId!, {
    query: { enabled: !!activeWorkspaceId },
  });
  const storesList = extractStoresArray(stores) as StoreItem[];

  const handleExportCSV = () => {
    if (customers.length === 0) {
      toast.error('Não há clientes disponíveis para exportar');
      return;
    }

    const headers = ['ID', 'Nome', 'Email', 'Telefone', 'ID da Loja', 'Total de Conversas', 'Última Atividade'];
    const rows = customers.map((c) => [
      `"${c.id || ''}"`,
      `"${c.customerName || c.name || ''}"`,
      `"${c.customerEmail || c.email || ''}"`,
      `"${c.customerPhone || c.phone || ''}"`,
      `"${c.storeId || ''}"`,
      c.totalConversations || 0,
      `"${c.lastActiveAt || ''}"`,
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `tales-customers-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exportou ${customers.length} registros de clientes para CSV`);
  };

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-card p-3 rounded-xl border border-border/60 shadow-sm">
      {/* Search Input */}
      <div className="relative flex-1 min-w-[240px]">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome, email ou telefone do cliente..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-8 pr-8 h-9 text-xs"
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Store Filter Selector */}
      <div className="w-full sm:w-[220px]">
        <Select value={selectedStoreId} onValueChange={onStoreChange}>
          <SelectTrigger className="h-9 text-xs">
            <div className="flex items-center gap-2 truncate">
              <Store className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <SelectValue placeholder="Todas as Lojas" />
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">
              Todas as Lojas Conectadas
            </SelectItem>
            {storesList.map((store) => (
              <SelectItem key={store.id} value={store.id} className="text-xs">
                {store.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCSV}
          className="gap-1.5 h-9 text-xs text-muted-foreground hover:text-foreground"
        >
          <Download className="h-3.5 w-3.5" />
          <span className="hidden md:inline">Exportar CSV</span>
        </Button>

        <Button
          size="sm"
          onClick={onAddCustomerClick}
          className="gap-1.5 h-9 text-xs shadow-sm font-medium"
        >
          <Plus className="h-4 w-4" />
          <span>Adicionar Cliente</span>
        </Button>
      </div>
    </div>
  );
};
